import { Component } from 'react'
import type { ErrorInfo, ReactNode } from 'react'

/**
 * กันหน้าขาว — React 19 ถอด component tree ทิ้งทั้งอันเมื่อมี error ตอน render
 * ผลคือผู้ใช้เห็น "จอขาว" ที่ไม่บอกอะไรเลย และคนแก้ก็ไล่ไม่ถูกว่าพังตรงไหน
 *
 * ⚠️ ครอบไว้ที่ Shell ไม่ใช่ที่รากของแอป — ถ้าครอบทั้งแอป แถบเมนูจะหายไปด้วย
 *    แล้วผู้ใช้ติดอยู่กับหน้าพังโดยไปหน้าอื่นไม่ได้ ต้องรีเฟรชเองเท่านั้น
 *
 * ⚠️ จับได้เฉพาะ error ตอน "render" — error ใน event handler หรือใน promise
 *    ที่ไม่ได้ await จะไม่เข้าที่นี่ (นั่นคือเหตุผลที่ทุกหน้าต้อง catch เองใน load())
 */
type Props = { children: ReactNode; onReset?: () => void }
type State = { error: Error | null; info: string | null }

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, info: null }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // ให้ขึ้น console ด้วย — คนที่เปิด devtools อยู่จะได้เห็น stack เต็ม ๆ
    console.error('[ui] หน้าพัง:', error, info.componentStack)
    this.setState({ info: info.componentStack ?? null })
  }

  render() {
    const { error, info } = this.state
    if (!error) return this.props.children

    return (
      <section className="card">
        <h2>หน้านี้แสดงผลไม่สำเร็จ</h2>
        <p className="meta">
          ข้อมูลไม่ได้เสียหาย — เป็นข้อผิดพลาดของหน้าจอเอง ลองกดโหลดใหม่
          ถ้ายังเป็นซ้ำ ส่งข้อความข้างล่างนี้ให้คนดูแลระบบ
        </p>

        <p className="error">
          {error.name}: {error.message}
        </p>

        {/* stack ยาวมาก พับไว้ ไม่ให้บังปุ่ม แต่ต้องคัดลอกได้ */}
        <details>
          <summary>รายละเอียดสำหรับคนแก้</summary>
          <pre className="crash-stack">{[error.stack, info].filter(Boolean).join('\n\n')}</pre>
        </details>

        <div className="row-actions">
          <button
            type="button"
            onClick={() => {
              this.setState({ error: null, info: null })
              this.props.onReset?.()
            }}
          >
            ลองแสดงใหม่
          </button>
          <button type="button" className="ghost" onClick={() => window.location.reload()}>
            โหลดหน้าใหม่ทั้งหมด
          </button>
        </div>
      </section>
    )
  }
}
